import { friendlyError } from '../../../utils/userMessages';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import { Phone as CallEnd, GraphicEq as Mic, VolumeOff as MicOff, Videocam, VideoSettings as VideocamOff, ArrowBack, People, Slideshow as ScreenShare, Slideshow as StopScreenShare } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { loadAgoraRtc, fetchAgoraToken, confirmMeetingJoin } from '../../../services/integrations/agora';
import { resolveCompanyId } from '../../../utils/companyIdentity';
import { useAuth } from '../../auth/AuthContext';
import toast from 'react-hot-toast';

const VideoCall = () => {
    const { meetingId: routeMeetingId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser, userData, companyData } = useAuth();
    const state = location.state || {};
    const meetingId = state.meetingId || routeMeetingId;

    const clientRef = useRef(null);
    const localTracksRef = useRef({ audio: null, video: null });
    const screenTrackRef = useRef(null);
    const agoraRef = useRef(null);
    const leavingRef = useRef(false);

    const [meeting, setMeeting] = useState(state.meetingData || null);
    const [status, setStatus] = useState('Preparing secure room…');
    const [joining, setJoining] = useState(true);
    const [error, setError] = useState('');
    const [micOn, setMicOn] = useState(true);
    const [cameraOn, setCameraOn] = useState(true);
    const [remoteUsers, setRemoteUsers] = useState([]);
    const [screenSharing, setScreenSharing] = useState(false);

    const cleanup = useCallback(async () => {
        if (leavingRef.current) return;
        leavingRef.current = true;
        try {
            const { audio, video } = localTracksRef.current;
            audio?.stop(); audio?.close();
            video?.stop(); video?.close();
            screenTrackRef.current?.stop?.(); screenTrackRef.current?.close?.();
            screenTrackRef.current = null;
            localTracksRef.current = { audio: null, video: null };
            if (clientRef.current) await clientRef.current.leave();
        } catch (e) {
            console.warn('Meeting cleanup warning:', e);
        } finally {
            clientRef.current = null;
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        const start = async () => {
            try {
                let meetingData = meeting;
                if (!meetingData && meetingId) {
                    const snap = await getDoc(doc(db, 'meetings', meetingId));
                    if (snap.exists()) {
                        meetingData = { id: snap.id, ...snap.data() };
                        if (!cancelled) setMeeting(meetingData);
                    }
                }

                const companyId = resolveCompanyId({ companyData, userData, currentUser });
                const channel = state.channelName || meetingData?.agoraChannel || `telcofair_${meetingId}`;
                if (!companyId && !(state.appId && state.token)) {
                    throw new Error('Company context is unavailable. Open the meeting from your company workspace and try again.');
                }

                const meetingHostCompanyId = meetingData?.companyId || meetingData?.requesterId || companyId;
                let rtcAuth = state.appId && state.token
                    ? { appId: state.appId, token: state.token, uid: state.uid || 0, channelName: channel }
                    : await fetchAgoraToken(meetingHostCompanyId, channel, currentUser?.uid || userData?.uid, 'publisher', meetingId);

                const AgoraRTC = await loadAgoraRtc();
                agoraRef.current = AgoraRTC;
                if (cancelled) return;
                setStatus('Connecting to meeting…');

                const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
                clientRef.current = client;

                client.on('user-published', async (user, mediaType) => {
                    await client.subscribe(user, mediaType);
                    if (mediaType === 'video') {
                        setRemoteUsers(prev => prev.some(u => u.uid === user.uid) ? prev : [...prev, user]);
                        setTimeout(() => user.videoTrack?.play(`remote-video-${user.uid}`), 0);
                    }
                    if (mediaType === 'audio') user.audioTrack?.play();
                });
                client.on('user-unpublished', (user, mediaType) => {
                    if (mediaType === 'video') setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
                });
                client.on('user-left', (user) => setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid)));

                await client.join(rtcAuth.appId, rtcAuth.channelName, rtcAuth.token, rtcAuth.uid);

                client.on('token-privilege-will-expire', async () => {
                    try {
                        const renewed = await fetchAgoraToken(meetingHostCompanyId, rtcAuth.channelName, currentUser?.uid || userData?.uid, 'publisher', meetingId);
                        await client.renewToken(renewed.token);
                        rtcAuth = renewed;
                        setStatus('Connected · security token renewed');
                    } catch (renewError) {
                        console.error('Agora token renewal failed:', renewError);
                        toast.error('Meeting security token could not be renewed.');
                    }
                });

                if (meetingId) {
                    try {
                        await confirmMeetingJoin(meetingId);
                    } catch (statusError) {
                        console.warn('Connected, but secure meeting join status could not be recorded:', statusError);
                    }
                }

                setStatus('Starting camera and microphone…');
                const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
                localTracksRef.current = { audio: audioTrack, video: videoTrack };
                if (cancelled) return;
                videoTrack.play('local-video');
                await client.publish([audioTrack, videoTrack]);

                setStatus('Connected');
                setJoining(false);
            } catch (e) {
                console.error('Video room initialization failed:', e);
                if (!cancelled) {
                    setError(e?.message || 'Unable to join the video meeting.');
                    setStatus('Connection failed');
                    setJoining(false);
                }
            }
        };

        start();
        return () => { cancelled = true; cleanup(); };
        // meeting intentionally comes from route/fetch once; rejoining on state updates would duplicate clients.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meetingId]);

    const toggleMic = async () => {
        const next = !micOn;
        try { await localTracksRef.current.audio?.setEnabled(next); setMicOn(next); }
        catch { toast.error('Could not change microphone state'); }
    };

    const toggleCamera = async () => {
        const next = !cameraOn;
        try { await localTracksRef.current.video?.setEnabled(next); setCameraOn(next); }
        catch { toast.error('Could not change camera state'); }
    };

    const stopScreenShare = useCallback(async () => {
        const screenTrack = screenTrackRef.current;
        const videoTrack = localTracksRef.current.video;
        try {
            if (screenTrack && clientRef.current) await clientRef.current.unpublish(screenTrack);
            screenTrack?.stop?.(); screenTrack?.close?.();
            screenTrackRef.current = null;
            if (videoTrack && clientRef.current) {
                await clientRef.current.publish(videoTrack);
                videoTrack.play('local-video');
            }
            setScreenSharing(false);
        } catch (error) {
            console.warn('Stop screen share warning:', error);
        }
    }, []);

    const toggleScreenShare = async () => {
        if (screenSharing) { await stopScreenShare(); return; }
        try {
            const AgoraRTC = agoraRef.current || await loadAgoraRtc();
            const created = await AgoraRTC.createScreenVideoTrack({}, 'disable');
            const screenTrack = Array.isArray(created) ? created[0] : created;
            if (!screenTrack) throw new Error('Screen sharing is not available in this browser.');
            const videoTrack = localTracksRef.current.video;
            if (videoTrack && clientRef.current) await clientRef.current.unpublish(videoTrack);
            await clientRef.current.publish(screenTrack);
            screenTrackRef.current = screenTrack;
            screenTrack.play('local-video');
            screenTrack.on?.('track-ended', stopScreenShare);
            setScreenSharing(true);
        } catch (error) {
            toast.error(friendlyError(error, 'Could not start screen sharing.'));
        }
    };

    const leave = async () => {
        await cleanup();
        navigate('/dashboard/meetings', { replace: true });
    };

    return (
        <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at 8% 0%, rgba(var(--primary-rgb),.08), transparent 28%), radial-gradient(circle at 94% 92%, rgba(var(--secondary-rgb),.07), transparent 30%), #F1F6F9', color: '#182235', p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ mb: 1.5, p: 1.2, px: 1.5, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, background: 'rgba(255,255,255,.94)', color: '#182235', border: '1px solid rgba(31,62,82,.085)', boxShadow: '0 10px 30px rgba(25,57,78,.085)', backdropFilter: 'blur(18px)'  }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                    <IconButton onClick={() => navigate('/dashboard/meetings')} sx={{ color: 'var(--primary-ink)', bgcolor: 'rgba(var(--primary-rgb),.06)' }}><ArrowBack /></IconButton>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meeting?.title || state.meetingTitle || 'Video Meeting'}</Typography>
                        <Typography sx={{ fontSize: '.7rem', color: '#6D7C91' }}>{status}</Typography>
                    </Box>
                </Box>
                <Chip icon={<People sx={{ color: 'var(--primary-ink) !important' }} />} label={`${remoteUsers.length + 1} participant${remoteUsers.length ? 's' : ''}`} size="small" sx={{ bgcolor: 'rgba(var(--primary-rgb),.07)', color: 'var(--primary-ink)', border: '1px solid rgba(var(--primary-rgb),.10)' }} />
            </Paper>

            <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: remoteUsers.length ? { xs: '1fr', md: '1fr 1fr' } : '1fr', gap: 1.5, minHeight: 0 }}>
                <Box sx={{ position: 'relative', minHeight: { xs: 330, md: 500 }, borderRadius: '22px', overflow: 'hidden', bgcolor: '#111b2d', border: '1px solid rgba(255,255,255,.08)' }}>
                    <div id="local-video" style={{ width: '100%', height: '100%', minHeight: 'inherit' }} />
                    <Box sx={{ position: 'absolute', left: 14, bottom: 14, px: 1.1, py: .5, borderRadius: 999, bgcolor: 'rgba(0,0,0,.45)', backdropFilter: 'blur(8px)', fontSize: '.72rem', fontWeight: 700 }}>
                        {state.userName || userData?.displayName || currentUser?.displayName || 'You'}
                    </Box>
                    {!cameraOn && <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: '#111b2d' }}><VideocamOff sx={{ fontSize: 48, color: 'rgba(255,255,255,.35)' }} /></Box>}
                </Box>

                {remoteUsers.map(user => (
                    <Box key={String(user.uid)} sx={{ position: 'relative', minHeight: { xs: 330, md: 500 }, borderRadius: '22px', overflow: 'hidden', bgcolor: '#111b2d', border: '1px solid rgba(255,255,255,.08)' }}>
                        <div id={`remote-video-${user.uid}`} style={{ width: '100%', height: '100%', minHeight: 'inherit' }} />
                        <Box sx={{ position: 'absolute', left: 14, bottom: 14, px: 1.1, py: .5, borderRadius: 999, bgcolor: 'rgba(0,0,0,.45)', backdropFilter: 'blur(8px)', fontSize: '.72rem', fontWeight: 700 }}>Participant {String(user.uid)}</Box>
                    </Box>
                ))}
            </Box>

            {(joining || error) && (
                <Box sx={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(2,6,23,.68)', backdropFilter: 'blur(8px)', zIndex: 20, p: 2 }}>
                    <Paper elevation={0} sx={{ width: 'min(440px, 92vw)', p: 3, textAlign: 'center', borderRadius: '22px', bgcolor: 'rgba(15,23,42,.94)', color: '#fff', border: '1px solid rgba(255,255,255,.12)' }}>
                        {joining ? <CircularProgress sx={{ color: 'var(--accent)' }} /> : <VideocamOff sx={{ fontSize: 42, color: '#fb7185' }} />}
                        <Typography sx={{ mt: 1.5, fontWeight: 800 }}>{joining ? status : 'Unable to join meeting'}</Typography>
                        {error && <Typography sx={{ mt: .8, fontSize: '.78rem', lineHeight: 1.65, color: 'rgba(255,255,255,.66)' }}>{error}</Typography>}
                        {error && <Button onClick={() => navigate('/dashboard/meetings')} sx={{ mt: 2, color: '#fff', borderColor: 'rgba(255,255,255,.24)' }} variant="outlined">Back to Meetings</Button>}
                    </Paper>
                </Box>
            )}

            <Paper elevation={0} sx={{ mt: 1.5, mx: 'auto', p: .8, px: 1.1, display: 'flex', justifyContent: 'center', gap: 1, borderRadius: '16px', background: 'rgba(255,255,255,.95)', border: '1px solid rgba(31,62,82,.085)', boxShadow: '0 12px 32px rgba(25,57,78,.10)', backdropFilter: 'blur(18px)' }}>
                <IconButton onClick={toggleMic} disabled={joining || !!error} sx={{ width: 46, height: 46, color: micOn ? 'var(--primary-ink)' : '#fff', bgcolor: micOn ? 'rgba(var(--primary-rgb),.07)' : 'rgba(239,68,68,.88)', border: micOn ? '1px solid rgba(var(--primary-rgb),.10)' : 'none', '&:hover': { bgcolor: micOn ? 'rgba(var(--primary-rgb),.12)' : 'rgba(239,68,68,1)' } }}>{micOn ? <Mic /> : <MicOff />}</IconButton>
                <IconButton onClick={toggleCamera} disabled={joining || !!error || screenSharing} sx={{ width: 46, height: 46, color: cameraOn ? 'var(--secondary-ink)' : '#fff', bgcolor: cameraOn ? 'rgba(var(--secondary-rgb),.08)' : 'rgba(239,68,68,.88)', border: cameraOn ? '1px solid rgba(var(--secondary-rgb),.11)' : 'none', '&:hover': { bgcolor: cameraOn ? 'rgba(var(--secondary-rgb),.13)' : 'rgba(239,68,68,1)' } }}>{cameraOn ? <Videocam /> : <VideocamOff />}</IconButton>
                <IconButton onClick={toggleScreenShare} disabled={joining || !!error} sx={{ width: 46, height: 46, color: screenSharing ? 'var(--secondary-contrast)' : 'var(--accent-ink)', bgcolor: screenSharing ? 'var(--secondary)' : 'rgba(var(--accent-rgb),.08)', border: '1px solid rgba(var(--accent-rgb),.10)', '&:hover': { bgcolor: screenSharing ? 'var(--secondary)' : 'rgba(var(--accent-rgb),.14)' } }}>{screenSharing ? <StopScreenShare /> : <ScreenShare />}</IconButton>
                <Button onClick={leave} startIcon={<CallEnd />} variant="contained" sx={{ px: 2.1, minHeight: 46, borderRadius: '12px !important', bgcolor: '#ef4444 !important', color: '#fff !important', boxShadow: '0 8px 18px rgba(239,68,68,.20) !important', '&:hover': { bgcolor: '#dc2626 !important' } }}>Leave</Button>
            </Paper>
        </Box>
    );
};

export default VideoCall;
